import { useEffect, useState } from 'react'
import RoutesMap from 'route_control/RouteControl'
import * as Icons from 'lucide-react'
import Menu from 'antd/es/menu'
import ConfigProvider from 'antd/es/config-provider'
import { Drawer } from 'antd'
import PerfectScrollbar from 'react-perfect-scrollbar'
import 'react-perfect-scrollbar/dist/css/styles.css'
import favicon from '../../assets/images/favicon.png'
import logo from '../../assets/images/logo.png'
import { toast } from 'react-toastify'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { sidebar, signOut } from '../../redux/slices/assets'
import { SessionManager } from 'utils'

export default function Sidebar(props) {

    const { collapsed, setCollapsed, drawerOpen, setDrawerOpen, isMobileOrTablet } = props

    const [loading, setLoading] = useState(true)
    const [stateOpenKeys, setStateOpenKeys] = useState(['0'])
    const [selectedKeys, setSelectedKeys] = useState([])
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const data = useSelector(state => state?.sidebar?.data)
    // console.log(data?.Permissions)

    const DynamicIcon = ({ iconName, ...props }) => {
        const IconComponent = Icons[iconName];

        if (!IconComponent) {
            // Handle cases where the icon name doesn't exist
            return <span>Icon not found</span>;
        }

        return <IconComponent {...props} />;
    }

    const item = data?.Permissions?.map((parent, p_index) => (
        {
            key: p_index.toString(),
            icon: <DynamicIcon iconName={parent.icon} size={16} />,
            label: parent?.module_name,
            children: parent?.masterAction?.map((child, c_index) =>
                child?.permissionAttributes?.action_attributes && JSON.parse(child?.permissionAttributes?.action_attributes)
                    .filter(fil => fil.show_in_menu === true)
                    .map((action, a_index) => (
                        {
                            key: p_index.toString() + c_index.toString() + a_index.toString(),
                            icon: <DynamicIcon iconName={'CircleDot'} size={16} />,
                            label: <Link to={action?.route}>{action?.sub_action_name}</Link>,
                        }
                    )
                    )).flat()
        }
    ))

    const getLevelKeys = (items1) => {
        const key = {};
        const func = (items2, level = 1) => {
            items2?.forEach((item) => {
                if (item?.key) {
                    key[item.key] = level;
                }
                if (item?.children) {
                    func(item.children, level + 1);
                }
            });
        };
        func(items1);
        return key;
    };

    const levelKeys = getLevelKeys(item);

    const onOpenChange = (openKeys) => {
        const currentOpenKey = openKeys.find((key) => stateOpenKeys.indexOf(key) === -1);
        // open
        if (currentOpenKey !== undefined) {
            const repeatIndex = openKeys
                .filter((key) => key !== currentOpenKey)
                .findIndex((key) => levelKeys[key] === levelKeys[currentOpenKey]);
            setStateOpenKeys(
                openKeys
                    // remove repeat key
                    .filter((_, index) => index !== repeatIndex)
                    // remove current level all child
                    .filter((key) => levelKeys[key] <= levelKeys[currentOpenKey]),
            );
        } else {
            // close
            setStateOpenKeys(openKeys);
        }
    };

    const sidebar_res = (res) => {
        if (res && res.status === 200) {
            setLoading(false)
            const permission = res?.data?.data?.attributes?.map(val => JSON.parse(val)).flat()
            SessionManager.setSession('permission', JSON.stringify(permission))
        } else {
            setLoading(false)
        }
    }

    const logout = () => {
        dispatch(signOut({ navigate })).then((res) => {
            const response = res.payload
            if (response?.status === 200) {
                // Clear all session data
                SessionManager.removeCookie("user_token");
                SessionManager.removeCookie("user");
                SessionManager.clearSession();
                // Navigate to login
                navigate(RoutesMap.LOGIN.path)
            } else if (response?.status === 401) {
                // Token expired, clear and redirect
                SessionManager.removeCookie("user_token");
                SessionManager.removeCookie("user");
                SessionManager.clearSession();
                navigate(RoutesMap.LOGIN.path)
            } else {
                // Show error toast for other statuses
                toast.error(response?.data?.message || "Something went wrong. Please try again.", { position: "bottom-right", closeOnClick: true });
            }
        })
    }

    useEffect(() => {
        setLoading(true)
        dispatch(sidebar({ navigate })).then((res) => sidebar_res(res.payload))
    }, [])

    useEffect(() => {
        // Derive selected and open keys from current URL
        const pathName = location?.pathname || ''
        let computedSelectedKey = null
        let computedOpenKey = null

        item?.forEach((parent) => {
            parent?.children?.forEach((child) => {
                const route = child?.label?.props?.to
                if (!route) return
                if (pathName === route || pathName.startsWith(route + '/')) {
                    computedSelectedKey = child?.key
                    computedOpenKey = parent?.key
                }
            })
        })

        if (computedSelectedKey) {
            setSelectedKeys([computedSelectedKey])
        }
        if (computedOpenKey) {
            setStateOpenKeys([computedOpenKey])
        }
    }, [location?.pathname, data])

    return (
        <>
            {!isMobileOrTablet && (
                <div className={collapsed ? 'sidebar_collapsed' : 'sidebar_notCollapsed'}>

                    {collapsed ?
                        <div className='sidebar_header'>
                            <img src={favicon} className='img-fluid' alt="logo" />
                        </div>
                        :
                        <div className='sidebar_header'>
                            <img src={logo} className='img-fluid' alt="logo" />
                        </div>
                    }

                    <ConfigProvider
                        theme={{
                            components: {
                                Menu: {
                                    itemSelectedColor: '#f6f4ec',
                                    subMenuItemSelectedColor: '#3A3F25',
                                    itemBg: '#f6f4ec',
                                    itemHoverBg: '#ebe5d5',
                                    itemHoverColor: '#3A3F25',
                                    itemSelectedBg: '#3A3F25',
                                    itemColor: '#000000',
                                    popupBg: '#f4f1e9'
                                },
                            },
                            token: {
                                fontFamily: 'Inter',
                                fontSize: 13,
                            },
                        }}
                    >
                        <PerfectScrollbar style={{
                            background: '#f4f1e9',
                            overflow: 'auto',
                            height: '100vh',
                            position: 'relative',
                            insetInlineStart: 0,
                        }}>
                            <Menu
                                mode="inline"
                                inlineCollapsed={collapsed}
                                selectedKeys={selectedKeys}
                                openKeys={stateOpenKeys}
                                onOpenChange={onOpenChange}
                                items={item}
                                style={{ backgroundColor: '#f4f1e9', marginBottom: '130px' }}
                            />
                            <div className={`px-4 py-3 sidebar_logout ${collapsed ? 'sidebar_logout_collapsed' : 'sidebar_logout_expanded'}`}>
                                {collapsed ?
                                    <Icons.Power size={18} onClick={() => logout()} />
                                    :
                                    <div className='d-flex gap-2 align-items-center' onClick={() => logout()}>
                                        <Icons.Power size={18} />
                                        <span className='fw-semibold text-secondary' style={{ fontSize: '12px' }}>Log Out</span>
                                    </div>
                                }
                            </div>
                        </PerfectScrollbar>
                    </ConfigProvider>
                </div>
            )}

            {isMobileOrTablet && (
                <Drawer
                    title={
                        <div className='sidebar_header d-flex gap-2 align-items-center justify-content-center'>
                            <img src={logo} className='img-fluid' alt="logo" />
                        </div>
                    }
                    placement="left"
                    closable
                    onClose={() => setDrawerOpen(false)}
                    open={drawerOpen}
                    zIndex={2000}
                    styles={{
                        body: { padding: 0, background: "#f4f1e9" },
                        header: { background: "#f4f1e9" },  // optional
                        footer: { background: "#f4f1e9" }   // optional
                    }}
                >
                    <ConfigProvider
                        theme={{
                            components: {
                                Menu: {
                                    itemSelectedColor: '#f6f4ec',
                                    subMenuItemSelectedColor: '#3A3F25',
                                    itemBg: '#f6f4ec',
                                    itemHoverBg: '#ebe5d5',
                                    itemHoverColor: '#3A3F25',
                                    itemSelectedBg: '#3A3F25',
                                    itemColor: '#000000',
                                    popupBg: '#f4f1e9'
                                },
                            },
                            token: {
                                fontFamily: 'Inter',
                                fontSize: 13,
                            },
                        }}
                    >
                        <PerfectScrollbar style={{
                            background: '#f4f1e9',
                            overflow: 'auto',
                            height: '100vh',
                            position: 'relative',
                            insetInlineStart: 0,
                        }}>
                            <Menu
                                mode="inline"
                                inlineCollapsed={collapsed}
                                selectedKeys={selectedKeys}
                                openKeys={stateOpenKeys}
                                onOpenChange={onOpenChange}
                                items={item}
                                style={{ backgroundColor: '#f4f1e9', marginBottom: '130px' }}
                            />
                            <div className={`px-4 py-3 sidebar_logout ${collapsed ? 'sidebar_logout_collapsed' : 'sidebar_logout_expanded'}`}>
                                {collapsed ?
                                    <Icons.Power size={18} onClick={() => logout()} />
                                    :
                                    <div className='d-flex gap-2 align-items-center' onClick={() => logout()}>
                                        <Icons.Power size={18} />
                                        <span className='fw-semibold text-secondary' style={{ fontSize: '12px' }}>Log Out</span>
                                    </div>
                                }
                            </div>
                        </PerfectScrollbar>
                    </ConfigProvider>
                </Drawer>
            )}
        </>
    )
}